/**
 * Coordinates campaign fields with the remaining program split-save fields so
 * each value is validated and persisted at its owning API boundary.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Coachmark from '@amzn/meridian/coachmark';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import { ObjectId } from 'bson';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useTranslation } from 'src/arb-wrapper';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { useUser } from 'src/contexts/UserContext';
import { CampaignDestinationUrlConfigTypes, CampaignPublishers, CampaignStatuses, BusinessLines, SportsBusinessLines } from 'src/store/constants';
import { FEATURE_FLAGS, DEFAULT_REFRESH_SCHEDULE_CRON, DEFAULT_SPORTS_REFRESH_SCHEDULE_CRON, ConfigurationType, NOTIFICATION_RECIPIENTS_NO_BUSINESS_UNIT_CODE, SONATA_PAGE_TYPES } from 'src/constants';
import {
  type CampaignPublishedStateDto,
  type CampaignDesiredStateDto,
  type CreateCampaignDto,
  type UpdateCampaignDto,
  type CampaignConfigurationDto,
  type ProgramDto,
  type Schedule,
  type CampaignConfigurationControllerFindManyApiResponse,
  type Country,
  type BusinessLine,
} from 'src/store/generated/leela-api-generated';
import { useCampaignConfigurationControllerFindManyQuery, useCampaignsControllerCreateMutation, useCampaignsControllerUpdateMutation, useCampaignsControllerValidateNotificationRecipientsMutation, useProgramsControllerUpdateMutation, leelaApi, type PublisherAccountConfigValue } from 'src/store/leela-api';
import { buildBusinessUnitFilters } from 'src/store/fetch-all-publisher-account-configs';
import type { LeelaFormData, ClonedCampaignData } from 'src/types/form.types';
import { getErrorsByField, parseGtisFromField } from 'src/utils';
import { isFeatureEnabled } from 'src/feature-flags';
import { groupStrategiesByAbTesting } from 'src/utils/abTestingUtils';
import { useAvailablePublishers } from 'src/hooks/use-available-publishers';
import { useAllowedScope } from 'src/hooks/useAllowedScope';
import { useMyPermissions } from 'src/hooks/useMyPermissions';
import { usePublisherConfigCountries } from 'src/hooks/usePublisherConfigCountries';
import { FormProductStrategies } from 'src/components/campaigns/forms/FormProductStrategies';
import { FormSchedule } from 'src/components/common/forms/FormSchedule';
import { FormInput } from 'src/components/common/forms/FormInput';
import { ContentRuleSelector } from 'src/components/campaigns/forms/ContentRuleSelector';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { FormSharedUserInput } from 'src/components/common/forms/FormSharedUserInput';
import { PublisherAccountFields } from 'src/components/publisher-config/PublisherAccountFields';
import { type RTKWrappedError } from 'src/components/common/forms/types';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'src/store';

interface FormCampaignEditProps {
  campaign?: CampaignDesiredStateDto;
  initialData?: ClonedCampaignData;
  program?: ProgramDto;
  blocklistConfigs?: CampaignConfigurationControllerFindManyApiResponse;
  blocklistSearch?: string;
  onBlocklistSearch?: (query: string) => void;
  isBlocklistLoading?: boolean;
  programId?: string;
  /** Called before creating a campaign to lazily obtain a programId. Receives program-level form fields. */
  onBeforeCreate?: (programFields: { schedule?: unknown; campaignName?: string; country?: string }) => Promise<string>;
  onCancel: () => void;
  onSave: () => void;
}

const defaultCampaign = {
  publisher: CampaignPublishers.META,
  status: CampaignStatuses.ACTIVE,
  placements: [],
  productStrategies: [],
  destinationUrlConfig: {
    type: CampaignDestinationUrlConfigTypes.DETAIL,
  },
};

const uniqueLowercased = (values: Array<string | undefined>): string[] => {
  const normalized = values
    .map((value) => value?.trim().toLowerCase())
    .filter((value): value is string => Boolean(value));
  return Array.from(new Set(normalized));
};

/**
 * Transforms GTI list from string to array, preferring validated GTIs
 * @param productStrategies - Array of product strategies
 * @param rawData - Raw form data containing gtiList_valid fields
 * @returns Transformed product strategies with gtiList as array
 */
export function transformGtiListForSubmission<T extends { gtiList?: string | string[] }> (
  productStrategies: T[],
  rawData: Record<string, unknown>
): T[] {
  return productStrategies.map((ps, index) => {
    if (ps.gtiList && typeof ps.gtiList === 'string') {
      // Use the validated GTI list (only valid GTIs with title data)
      const rawDataTyped = rawData as Record<string, unknown>;
      const validGtiList = (rawDataTyped.productStrategies as Array<Record<string, unknown>>)?.[index]?.gtiList_valid as string | undefined;
      const gtiListToUse = validGtiList ?? ps.gtiList;

      return {
        ...ps,
        gtiList: parseGtisFromField(gtiListToUse as string),
      };
    }
    return ps;
  });
}

/**
 * Fans a sports product strategy's single, strategy-level schedule (collected once via
 * `StrategyScheduleWindows`) onto every one of its bender templates, then strips the transient
 * strategy-level fields. Each template ends up with the same `config.liveStatuses` + `scheduleWindows`,
 * so the persisted per-template shape (consumed by the asset-schedule read path, keyed by templateConfigId)
 * is identical to what the old per-template UI produced. Run before {@link pruneScheduleWindows}, which
 * then trims each template's windows down to its checked statuses. Mutates and returns the same array.
 */
export function applyStrategyScheduleToTemplates<T extends { benderTemplates?: unknown[] }> (
  productStrategies: T[],
): T[] {
  for (const ps of productStrategies) {
    const strategy = ps as T & { liveStatuses?: string[]; scheduleWindows?: Record<string, unknown> };
    const { liveStatuses, scheduleWindows } = strategy;
    delete strategy.liveStatuses;
    delete strategy.scheduleWindows;

    // Only sports strategies carry a strategy-level schedule; skip when neither field is present.
    if (liveStatuses === undefined && scheduleWindows === undefined) continue;

    for (const rawTemplate of ps.benderTemplates ?? []) {
      const template = rawTemplate as { config?: { liveStatuses?: string[] }; scheduleWindows?: Record<string, unknown> };
      template.config = { ...template.config, liveStatuses: liveStatuses ?? [] };
      // Deep-clone so no two templates alias the same mutable schedule-window objects.
      template.scheduleWindows = scheduleWindows
        ? JSON.parse(JSON.stringify(scheduleWindows)) as Record<string, unknown>
        : undefined;
    }
  }
  return productStrategies;
}

/**
 * Prunes each bender template's `scheduleWindows` map down to the live statuses still selected in
 * `config.liveStatuses`. The form intentionally retains windows for unchecked statuses (so re-checking
 * restores them within a session), so this strips them at submit time to avoid persisting stale windows.
 * Mutates and returns the same product-strategies array.
 */
export function pruneScheduleWindows<T extends { benderTemplates?: unknown[] }> (
  productStrategies: T[],
): T[] {
  for (const ps of productStrategies) {
    for (const rawTemplate of ps.benderTemplates ?? []) {
      const template = rawTemplate as { config?: { liveStatuses?: string[] }; scheduleWindows?: Record<string, unknown> };
      const scheduleWindows = template.scheduleWindows;
      if (!scheduleWindows) continue;

      const checked = template.config?.liveStatuses ?? [];
      const pruned = Object.fromEntries(
        Object.entries(scheduleWindows).filter(([status]) => checked.includes(status)),
      );
      template.scheduleWindows = Object.keys(pruned).length > 0 ? pruned : undefined;
    }
  }
  return productStrategies;
}

export const FormCampaignEdit: React.FC<FormCampaignEditProps> = (props) => {
  const { campaign, initialData, program, programId, onBeforeCreate, blocklistConfigs, blocklistSearch, onBlocklistSearch, isBlocklistLoading, onCancel, onSave } = props;
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const { user: currentUser } = useUser();
  const availablePublishers = useAvailablePublishers();
  const { countries: allowedCountries, publishers: allowedPublishers, businessLines: allowedBusinessLines, businessUnitIds } = useAllowedScope('MANAGE_CAMPAIGNS');
  const { isPlatformAdmin } = useMyPermissions();
  const publisherAccountBusinessUnitIds = isPlatformAdmin ? [] : businessUnitIds;
  const publisherAccountFilters = buildBusinessUnitFilters(publisherAccountBusinessUnitIds);
  const [createCampaign] = useCampaignsControllerCreateMutation();
  const [updateCampaign] = useCampaignsControllerUpdateMutation();
  const [validateNotificationRecipients] = useCampaignsControllerValidateNotificationRecipientsMutation();
  const [updateProgram] = useProgramsControllerUpdateMutation();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const isNotificationRecipientsEnabled = isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST);

  // Prefetch all Sonata page types on form mount so the page ID dropdown is instant
  useEffect(() => {
    SONATA_PAGE_TYPES.forEach(pt => {
      dispatch(leelaApi.endpoints.sonataApiControllerSearchPageIds.initiate(
        { pageType: pt },
        { subscribe: false },
      ));
    });
  }, [dispatch]);

  const saveProgramFields = async (targetProgramId: string, rawData: LeelaFormData) => {
    if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) || programInitializedRef.current !== targetProgramId) return;

    const response = await updateProgram({
      id: targetProgramId,
      updateProgramDto: {
        schedule: rawData.schedule as unknown as Schedule,
      },
    });
    if ('error' in response) {
      createToast({ type: AlertType.WARNING, message: t('form_campaign_error_save') });
    }
  };

  const existingStrategies = (campaign as unknown as { productStrategies?: Array<{ country?: string; businessLine?: string }> })?.productStrategies;

  const isCreating = campaign === undefined;
  const creatorEmail = uniqueLowercased([currentUser?.email])[0];
  const initialNotificationRecipients = campaign
    ? campaign.notificationRecipients ?? []
    : isNotificationRecipientsEnabled
      ? uniqueLowercased([
        ...(initialData?.notificationRecipients ?? []),
        creatorEmail,
      ])
      : [];

  const validateNotificationRecipient = async (email: string): Promise<string | null> => {
    if (!campaign?.campaignId) return null;

    try {
      const result = await validateNotificationRecipients({
        id: campaign.campaignId,
        validateNotificationRecipientsDto: { emails: [email] },
      }).unwrap();
      return result.eligible.includes(email)
        ? null
        : t('notification_recipient_ineligible');
    } catch (error) {
      const data = (error as FetchBaseQueryError).data;
      if (
        typeof data === 'object' &&
        data !== null &&
        'code' in data &&
        data.code === NOTIFICATION_RECIPIENTS_NO_BUSINESS_UNIT_CODE
      ) {
        return t('notification_recipient_no_business_unit');
      }
      throw error;
    }
  };

  const formMethods = useForm<LeelaFormData>({
    defaultValues: {
      ...defaultCampaign,
      ...(!campaign && initialData ? initialData as unknown as LeelaFormData : {}),
      ...(campaign as unknown as LeelaFormData),
      // Transform configIds array for form
      configIds: campaign?.configIds ?? [],
      notificationRecipients: initialNotificationRecipients,
      // Transient (form-only) holder for content-rule config selections. It is folded into
      // configIds on save and then removed, so it is never sent to the API as its own field.
      contentRuleConfigIds: [],
      schedule: { refreshScheduleCron: DEFAULT_REFRESH_SCHEDULE_CRON },
      publisherAccountConfigId: '',
      campaignCountry: existingStrategies?.[0]?.country ?? (initialData as Record<string, unknown>)?.campaignCountry as string ?? '',
      campaignBusinessLine: existingStrategies?.[0]?.businessLine ?? (initialData as Record<string, unknown>)?.campaignBusinessLine as string ?? '',
    },
    mode: 'onTouched',
  });

  const creatorRecipientInitializedRef = useRef(Boolean(creatorEmail));
  useEffect(() => {
    if (!isNotificationRecipientsEnabled || !isCreating || creatorRecipientInitializedRef.current || !creatorEmail) return;

    const recipients = formMethods.getValues('notificationRecipients') as string[] | undefined;
    formMethods.setValue(
      'notificationRecipients',
      uniqueLowercased([...(recipients ?? []), creatorEmail]),
    );
    creatorRecipientInitializedRef.current = true;
  }, [creatorEmail, formMethods, isCreating, isNotificationRecipientsEnabled]);

  const { reset } = formMethods;
  const programInitializedRef = useRef<string | null>(null);
  useEffect(() => {
    if (program && programId && programInitializedRef.current !== programId) {
      programInitializedRef.current = programId;
      reset((prev) => ({
        ...prev,
        schedule: (program.schedule ?? { refreshScheduleCron: DEFAULT_REFRESH_SCHEDULE_CRON }) as unknown as Record<string, unknown>,
      }));
    }
  }, [program, programId, reset]);

  useEffect(() => {
    // This use effect will disable save button when form methods has been changed
    // SIDE EFFECT: This has caused force re-renders which eliminates the ~5 second
    // delay that happens when changing templates
    const subscription = formMethods.watch((_value, { type }) => {
      if (type === 'change') {
        setIsLoading(true);

        // Use Promise.resolve to wait for the next tick
        Promise.resolve().then(() => {
          setIsLoading(false);
        });
      }
    });

    return () => { subscription.unsubscribe(); };
  }, [formMethods]);

  const { clearErrors, control, formState, handleSubmit, setError } = formMethods;
  const selectedPublisher = useWatch({ control, name: 'publisher' }) as CampaignPublishedStateDto['publisher'] ?? '';
  const campaignCountry = useWatch({ control, name: 'campaignCountry' }) as string ?? '';
  const campaignBusinessLine = useWatch({ control, name: 'campaignBusinessLine' }) as string ?? '';

  const { countries: publisherCountries, isLoading: isPublisherCountriesLoading } = usePublisherConfigCountries(selectedPublisher || undefined, publisherAccountBusinessUnitIds);
  const campaignCountryOptions = useMemo(
    () => publisherCountries
      .filter(c => allowedCountries.includes(c))
      .map(c => ({ label: c, value: c })),
    [publisherCountries, allowedCountries],
  );

  // Fetch publisher account configs filtered by selected publisher, country, business line, and the caller's business units
  const { data: publisherAccountConfigs } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.PUBLISHER_ACCOUNT,
    publisher: selectedPublisher || undefined,
    ...(campaignCountry && { country: campaignCountry as Country }),
    ...(campaignBusinessLine && { businessLine: campaignBusinessLine as BusinessLine }),
    ...(publisherAccountFilters && { filters: publisherAccountFilters }),
    includeCampaignCount: false,
  }, {
    skip: !selectedPublisher || !isCreating,
  });

  // On edit: fetch the existing publisher account config by ID from configIds
  const configIds = campaign?.configIds ?? [];
  const { data: existingPublisherConfig } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.PUBLISHER_ACCOUNT,
    includeCampaignCount: false,
    filters: { _id: { $in: configIds } },
  }, {
    skip: isCreating || !configIds.length,
  });

  const existingPublisherConfigItem = existingPublisherConfig?.data?.[0];

  useEffect(() => {
    if (existingPublisherConfigItem) {
      formMethods.setValue('publisherAccountConfigId', existingPublisherConfigItem.id);
    }
  }, [existingPublisherConfigItem, formMethods]);

  const selectedPublisherConfigId = useWatch({ control, name: 'publisherAccountConfigId' }) as string ?? '';
  const selectedPublisherConfig = useMemo(() => {
    if (!selectedPublisherConfigId || !publisherAccountConfigs?.data) return undefined;
    return publisherAccountConfigs.data.find((c: CampaignConfigurationDto) => c.id === selectedPublisherConfigId);
  }, [selectedPublisherConfigId, publisherAccountConfigs]);

  // Resolve the active publisher config across modes (create uses the filtered list, edit uses the
  // config fetched by id from configIds) so the schedule inputs can label times in its timezone.
  const activePublisherConfig = selectedPublisherConfig ?? existingPublisherConfigItem;
  const publisherTimezone = (activePublisherConfig?.config as PublisherAccountConfigValue | undefined)?.timezone ?? '';

  // On edit: fetch already-selected blocklist configs by ID so they always appear in dropdown
  const existingBlocklistConfigIds = campaign?.configIds ?? [];
  const { data: existingBlocklistConfigs } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    includeCampaignCount: false,
    filters: { _id: { $in: existingBlocklistConfigIds } },
  }, {
    skip: isCreating || !existingBlocklistConfigIds.length,
  });

  const filteredBlocklistOptions = useMemo(() => {
    const searchResults = (blocklistConfigs?.data ?? []).map((config: CampaignConfigurationDto) => ({
      label: config.name ?? config.id,
      value: config.id,
    }));

    // Merge already-selected blocklists (edit mode) so they always appear
    const existing = (existingBlocklistConfigs?.data ?? []).map((config: CampaignConfigurationDto) => ({
      label: config.name ?? config.id,
      value: config.id,
    }));

    const seen = new Set(searchResults.map(o => o.value));
    return [...searchResults, ...existing.filter(o => !seen.has(o.value))];
  }, [blocklistConfigs, existingBlocklistConfigs]);

  const publisherConfigOptions = useMemo(() => {
    if (!publisherAccountConfigs?.data) return [];
    return publisherAccountConfigs.data.map((config: CampaignConfigurationDto) => ({
      label: config.name ?? config.id,
      value: config.id,
    }));
  }, [publisherAccountConfigs]);

  const isAssetEvaluationEnabled = isFeatureEnabled(FEATURE_FLAGS.ENABLE_ASSET_EVAL_TRIGGER);
  // Show rules applicable to the campaign's publisher and business line, in both create and edit.
  // The backend treats a rule's empty/absent supportedPublishers or supportedBusinessLines as
  // "applies to all", so unscoped rules always appear; a rule scoped to other publishers/business
  // lines is filtered out. Mirrors the publisher-account config query above. Disabled rules are
  // excluded: the evaluation pipeline skips them (findEvaluatableContentRules), so offering them
  // as options would let users attach rules that silently do nothing.
  const { data: contentRuleConfigs, isFetching: isContentRuleLoading } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.CONTENT_RULE,
    includeCampaignCount: false,
    filters: { 'config.enabled': { $ne: false } },
    ...(selectedPublisher && { publisher: selectedPublisher }),
    ...(campaignBusinessLine && { businessLine: campaignBusinessLine as BusinessLine }),
  }, {
    skip: !isAssetEvaluationEnabled,
  });

  // On edit, load the rules already attached to the campaign (by id) so they can be pre-selected
  // and stay selectable even if they now fall outside the campaign's publisher/business-line
  // scope. Mirrors the blocklist existing+search merge above. Deliberately NOT filtered by
  // enabled: a rule that was attached and later disabled must stay visible as a chip so the
  // user can see it and remove it.
  const { data: attachedContentRuleConfigs } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.CONTENT_RULE,
    includeCampaignCount: false,
    filters: { _id: { $in: configIds } },
  }, {
    skip: !isAssetEvaluationEnabled || isCreating || !configIds.length,
  });

  const contentRuleOptions = useMemo(() => {
    const toOption = (config: CampaignConfigurationDto) => ({ label: config.name ?? config.id, value: config.id });
    const applicable = (contentRuleConfigs?.data ?? []).map(toOption);
    const attached = (attachedContentRuleConfigs?.data ?? []).map(toOption);
    const seen = new Set(applicable.map((o) => o.value));
    return [...applicable, ...attached.filter((o) => !seen.has(o.value))];
  }, [contentRuleConfigs, attachedContentRuleConfigs]);

  // On edit, pre-select the campaign's already-attached rules once, so the user's subsequent
  // add/remove edits are not clobbered by re-renders. Create mode starts with no rules selected.
  const contentRulesInitializedRef = React.useRef(false);
  useEffect(() => {
    if (isCreating || contentRulesInitializedRef.current || !attachedContentRuleConfigs?.data) return;
    formMethods.setValue(
      'contentRuleConfigIds',
      attachedContentRuleConfigs.data.map((config: CampaignConfigurationDto) => config.id),
    );
    contentRulesInitializedRef.current = true;
  }, [attachedContentRuleConfigs, formMethods, isCreating]);

  const selectedContentRuleIds = (useWatch({ control, name: 'contentRuleConfigIds' }) as string[] | undefined) ?? [];

  // When the campaign's publisher or business line changes, clear the selected rules: the
  // selection was made against the previous scope's applicable-rule list, and carrying it over
  // could attach rules that do not apply to the new publisher/business-line combination. The
  // previous scope is tracked in a ref so this fires only on a genuine change — not on the
  // initial render or edit-mode hydration, where the pre-selection of already-attached rules
  // must survive.
  const contentRuleScopeRef = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    const scope = `${selectedPublisher}|${campaignBusinessLine}`;
    if (contentRuleScopeRef.current !== undefined && contentRuleScopeRef.current !== scope) {
      formMethods.setValue('contentRuleConfigIds', [], { shouldDirty: true, shouldValidate: true });
    }
    contentRuleScopeRef.current = scope;
  }, [selectedPublisher, campaignBusinessLine, formMethods]);

  const removeContentRule = (id: string) => {
    formMethods.setValue(
      'contentRuleConfigIds',
      selectedContentRuleIds.filter((ruleId) => ruleId !== id),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  // Editable content-rule selector shared by create and edit (see ContentRuleSelector).
  // `disabled` is used in create mode until a publisher and business line are chosen; edit mode
  // always has both, so it stays enabled.
  const renderContentRuleSelector = (disabled: boolean) => (
    <ContentRuleSelector
      control={control}
      disabled={disabled}
      loading={isContentRuleLoading}
      options={contentRuleOptions}
      selectedIds={selectedContentRuleIds}
      onRemove={removeContentRule}
    />
  );

  const noCampaignCountryAvailable = !!selectedPublisher && !isPublisherCountriesLoading && campaignCountryOptions.length === 0;
  const noPublisherAccountAvailable = isCreating && (
    noCampaignCountryAvailable ||
    (!!campaignCountry && !!campaignBusinessLine && publisherConfigOptions.length === 0)
  );

  // Auto-select first publisher config when options change (create mode only)
  const prevPublisher = React.useRef(selectedPublisher);
  useEffect(() => {
    if (!isCreating) return;

    const publisherChanged = prevPublisher.current !== selectedPublisher;
    prevPublisher.current = selectedPublisher;

    // Clear when prerequisites are missing
    if (!campaignCountry || !campaignBusinessLine) {
      formMethods.setValue('publisherAccountConfigId', '');
      return;
    }

    if (publisherConfigOptions.length > 0) {
      const currentValue = formMethods.getValues('publisherAccountConfigId') as string;
      const currentStillValid = publisherConfigOptions.some(o => o.value === currentValue);
      if (publisherChanged || !currentStillValid) {
        formMethods.setValue('publisherAccountConfigId', publisherConfigOptions[0].value);
      }
    } else if (publisherChanged) {
      formMethods.setValue('publisherAccountConfigId', '');
    }
  }, [publisherConfigOptions, selectedPublisher, campaignCountry, campaignBusinessLine, formMethods, isCreating]);

  // Default the refresh schedule to daily for sports business lines and weekly
  // otherwise. Only applied when the schedule is still at one of the known
  // defaults, so a schedule the user has customized is never overwritten.
  const prevBusinessLine = useRef(campaignBusinessLine);
  useEffect(() => {
    if (prevBusinessLine.current === campaignBusinessLine) return;
    prevBusinessLine.current = campaignBusinessLine;

    const isSports = Object.values(SportsBusinessLines).includes(campaignBusinessLine as BusinessLine);
    const desiredCron = isSports ? DEFAULT_SPORTS_REFRESH_SCHEDULE_CRON : DEFAULT_REFRESH_SCHEDULE_CRON;
    const currentCron = (formMethods.getValues('schedule') as { refreshScheduleCron?: string })?.refreshScheduleCron;

    const isAtDefault = currentCron === DEFAULT_REFRESH_SCHEDULE_CRON || currentCron === DEFAULT_SPORTS_REFRESH_SCHEDULE_CRON;
    if (isAtDefault && currentCron !== desiredCron) {
      formMethods.setValue('schedule', { refreshScheduleCron: desiredCron });
    }
  }, [campaignBusinessLine, formMethods]);

  const handleResponse = (
    response: { data: CampaignDesiredStateDto } | { error: unknown }
  ) => {
    if ('data' in response) {
      clearErrors();
      onSave();
    } else {
      const {
        data: { error: errorMsg, message },
      } = response.error as RTKWrappedError;

      if (message && typeof message === 'object') {
        const errors = getErrorsByField(message);
        for (const [field, fieldMsg] of Object.entries(errors)) {
          setError(field, { message: fieldMsg });
        }
      }

      createToast({
        type: AlertType.ERROR,
        message: `${t('form_campaign_error_save')}: ${typeof message === 'string' ? message : errorMsg}`,
      });
    }
  };

  const mergePublisherConfigId = (data: Record<string, unknown>) => {
    const pubConfigId = data.publisherAccountConfigId as string | undefined;
    delete data.publisherAccountConfigId;
    if (pubConfigId) {
      const existing = (data.configIds as string[] | undefined) ?? [];
      data.configIds = [...existing.filter(id => id !== pubConfigId), pubConfigId];
    }
  };

  // Fold the selected content-rule config ids into the campaign's configIds, then drop the
  // transient form-only field so it is not sent to the API. This REPLACES the campaign's existing
  // content-rule subset with the current selection — previously-attached rule ids are stripped
  // first — so both additions and removals persist. Publisher-account and blocklist config ids are
  // preserved untouched.
  const mergeContentRuleConfigIds = (data: Record<string, unknown>) => {
    const selectedRuleIds = (data.contentRuleConfigIds as string[] | undefined) ?? [];
    delete data.contentRuleConfigIds;

    const previouslyAttachedRuleIds = (attachedContentRuleConfigs?.data ?? []).map((config: CampaignConfigurationDto) => config.id);
    const existing = (data.configIds as string[] | undefined) ?? [];
    const merged = existing.filter((id) => !previouslyAttachedRuleIds.includes(id));
    for (const id of selectedRuleIds) {
      if (!merged.includes(id)) merged.push(id);
    }
    data.configIds = merged;
  };

  const applyCampaignLevelFields = (data: Record<string, unknown>) => {
    const country = data.campaignCountry as string;
    const businessLine = data.campaignBusinessLine as string;
    delete data.campaignCountry;
    delete data.campaignBusinessLine;
    if (country && data.productStrategies) {
      (data.productStrategies as Array<Record<string, unknown>>).forEach(s => {
        s.country = country;
        if (businessLine) s.businessLine = businessLine;
      });
    }
  };

  const createCampaignHandler = async (rawData: LeelaFormData) => {
    // Resolve programId — use onBeforeCreate if provided (lazy program creation)
    let resolvedProgramId = programId ?? '';
    if (onBeforeCreate && !resolvedProgramId) {
      try {
        const formSchedule = (rawData as Record<string, unknown>).schedule;
        const formName = (rawData as Record<string, unknown>).name as string | undefined;
        const strategies = (rawData as Record<string, unknown>).productStrategies as Array<{ country?: string }> | undefined;
        const formCountry = strategies?.[0]?.country;
        resolvedProgramId = await onBeforeCreate({
          schedule: formSchedule,
          campaignName: formName,
          country: formCountry,
        });
      } catch {
        createToast({
          type: AlertType.ERROR,
          message: t('campaign_create_error'),
        });
        return;
      }
    }

    const { programId: _programId = resolvedProgramId, ...data } =
      rawData as unknown as CreateCampaignDto;

    delete (data as Partial<CampaignDesiredStateDto>).campaignId;
    delete (data as Record<string, unknown>).schedule;

    // Transform gtiList from string to array and handle A/B testing
    if (data.productStrategies) {
      data.productStrategies = transformGtiListForSubmission(data.productStrategies, rawData);

      // Process strategies - assign group IDs to all strategies
      const expandedStrategies: CreateCampaignDto['productStrategies'] = [];

      for (const strategy of data.productStrategies) {
        const strategyWithAB = strategy as typeof strategy & { abTestingEnabled?: boolean; abTestingVariations?: number };
        if (strategyWithAB.abTestingEnabled && strategyWithAB.abTestingVariations && strategyWithAB.abTestingVariations > 1) {
          // Generate a shared group ID for all A/B testing variations
          const sharedGroupId = new ObjectId().toHexString();

          // Create multiple variations of the same strategy
          for (let i = 0; i < strategyWithAB.abTestingVariations; i++) {
            const variation = {
              ...strategy,
              groupId: sharedGroupId,
              // Remove A/B testing specific fields and product set ID (will be auto-generated)
              productSetId: undefined,
              // Variations are clones of one strategy, so they share a name. Suffix each so the
              // per-campaign name-uniqueness check doesn't reject the save.
              ...(strategy.name ? { name: `${strategy.name} (${i + 1})` } : {}),
            };

            // Remove A/B testing control fields
            const { abTestingEnabled, abTestingVariations, ...cleanVariation } = variation as typeof variation & { abTestingEnabled?: boolean; abTestingVariations?: number };

            expandedStrategies.push(cleanVariation);
          }
        } else {
          // Regular strategy without A/B testing - only add groupId if A/B testing feature is enabled
          const { abTestingEnabled, abTestingVariations, ...regularStrategy } = strategyWithAB;
          const strategyToAdd = isFeatureEnabled(FEATURE_FLAGS.ENABLE_AB_TESTING)
            ? { ...regularStrategy, groupId: new ObjectId().toHexString() }
            : regularStrategy;
          expandedStrategies.push(strategyToAdd);
        }
      }

      data.productStrategies = expandedStrategies;
      applyStrategyScheduleToTemplates(data.productStrategies);
      pruneScheduleWindows(data.productStrategies);
    }

    mergePublisherConfigId(data as unknown as Record<string, unknown>);
    mergeContentRuleConfigIds(data as unknown as Record<string, unknown>);
    applyCampaignLevelFields(data as unknown as Record<string, unknown>);

    const { notificationRecipients = [], ...campaignData } = data;
    const notificationRecipientsPayload =
      !isNotificationRecipientsEnabled ||
      (creatorEmail === undefined && notificationRecipients.length === 0)
        ? {}
        : { notificationRecipients };
    const response = await createCampaign({
      createCampaignDto: {
        programId: _programId,
        ...campaignData,
        ...notificationRecipientsPayload,
      },
    });

    if (!('error' in response)) {
      await saveProgramFields(_programId, rawData);
    }

    handleResponse(response);
  };

  const updateCampaignHandler = async (rawData: LeelaFormData) => {
    const { campaignId, ...data } = {
      ...campaign!,
      ...(rawData as unknown as CampaignDesiredStateDto),
    };

    delete (data as Partial<UpdateCampaignDto>).adSets;
    delete (data as Record<string, unknown>).schedule;

    // Transform gtiList from string to array in productStrategies
    if (data.productStrategies) {
      data.productStrategies = transformGtiListForSubmission(data.productStrategies, rawData);

      // Sync edits from the representative strategy to all sibling strategies in A/B testing groups.
      // The form only renders the representative (first) strategy for each group, so changes to
      // templates, strategy IDs, locales, etc. need to be propagated to the other group members.
      const { abTestingGroups } = groupStrategiesByAbTesting(data.productStrategies);

      for (const group of abTestingGroups) {
        const representative = data.productStrategies[group.representativeIndex];
        for (const { index } of group.strategies) {
          if (index === group.representativeIndex) continue;

          const sibling = data.productStrategies[index];
          data.productStrategies[index] = {
            ...sibling,
            benderTemplates: representative.benderTemplates,
            strategyId: representative.strategyId,
            pageId: representative.pageId,
            gtiList: representative.gtiList,
            locales: representative.locales,
            country: representative.country,
            businessLine: representative.businessLine,
          };
        }
      }

      // Clean up A/B testing fields for updates (should not be present in edit mode)
      data.productStrategies = data.productStrategies.map((strategy) => {
        const { abTestingEnabled, abTestingVariations, ...cleanStrategy } = strategy as typeof strategy & { abTestingEnabled?: boolean; abTestingVariations?: number };
        return cleanStrategy;
      });

      applyStrategyScheduleToTemplates(data.productStrategies);
      pruneScheduleWindows(data.productStrategies);
    }

    mergePublisherConfigId(data as unknown as Record<string, unknown>);
    // Fold the rule selection into configIds with replace semantics (previously-attached rule
    // ids stripped, current selection added, publisher/blocklist ids untouched) — the same merge
    // the create path uses. It also removes the transient contentRuleConfigIds form field.
    mergeContentRuleConfigIds(data as unknown as Record<string, unknown>);
    delete (data as Record<string, unknown>).campaignCountry;
    delete (data as Record<string, unknown>).campaignBusinessLine;

    const shouldSendNotificationRecipients = isNotificationRecipientsEnabled && Boolean(
      formState.dirtyFields.notificationRecipients,
    );
    if (!shouldSendNotificationRecipients) {
      delete (data as Partial<UpdateCampaignDto>).notificationRecipients;
    }

    const response = await updateCampaign({
      id: campaignId,
      updateCampaignDto: data as UpdateCampaignDto,
    });

    if (!('error' in response)) {
      await saveProgramFields(programId ?? '', rawData);
    }

    handleResponse(response);
  };

  const handleSave = async () => {
    await handleSubmit(async (formData) => {
      const submitHandler =
        campaign && 'campaignId' in campaign
          ? updateCampaignHandler
          : createCampaignHandler;
      await submitHandler(formData);
    })();
  };

  return (
    <Box spacingInset="500" type="outline">
      <Column>
        <FormProvider {...formMethods} >
        <Row alignmentHorizontal="justify" widths={['fill', 'fill']}>
          <FormSelect
            control={control}
            data-testid="campaign-edit-publisher-select"
            disabled={!isCreating} // Write once field
            label={t('form_campaigns_publisher')}
            name="publisher"
            options={availablePublishers
              .filter(publisher => !isCreating || allowedPublishers.includes(publisher))
              .map((publisher) => ({
                label: publisher,
                value: publisher,
              }))}
            required
          />

          <FormSelect
            control={control}
            data-testid="campaign-edit-status-select"
            label={t('form_global_status')}
            name="status"
            options={Object.values(CampaignStatuses).map((status: CampaignDesiredStateDto['status']) => ({
              label: status,
              value: status,
            }))}
            required
          />
        </Row>

        {selectedPublisher && (
          <>
          {isCreating && (
            <Row widths={['fill', 'fill']}>
              <FormSelect
                control={control}
                data-testid="campaign-edit-country-select"
                disabled={isPublisherCountriesLoading}
                label={t('form_country')}
                loading={isPublisherCountriesLoading}
                name="campaignCountry"
                options={campaignCountryOptions}
                required
              />
              <FormSelect
                control={control}
                data-testid="campaign-edit-business-line-select"
                label={t('form_business_line')}
                name="campaignBusinessLine"
                options={[
                  ...Object.values(BusinessLines),
                  ...(isFeatureEnabled(FEATURE_FLAGS.ENABLE_SPORTS_BUSINESS_LINES) ? Object.values(SportsBusinessLines) : []),
                ]
                  .filter(bl => allowedBusinessLines.includes(bl))
                  .map(bl => ({
                    label: bl,
                    value: bl,
                  }))}
                required
              />
            </Row>
          )}

          {isCreating
            ? (
            <>
              {/* Fixed-width selector column: the publisher-account fields on the left vary by
                  publisher (TikTok renders extra ids), and a 'fill' width let them squeeze the
                  rule dropdown to a different size per publisher. 50% keeps it the same size as the other form fields while staying deterministic. */}
              <Row alignmentVertical="top" widths={['fill', '50%']}>
                <Column spacing="300">
                  <FormSelect
                    control={control}
                    data-testid="campaign-edit-publisher-config-select"
                    disabled={!campaignCountry || !campaignBusinessLine}
                    label={t('publisher_config')}
                    name="publisherAccountConfigId"
                    options={publisherConfigOptions}
                  />
                  {selectedPublisherConfig && (
                    <PublisherAccountFields hideMinDailyBudget publisher={selectedPublisher} values={selectedPublisherConfig.config as Record<string, unknown>} />
                  )}
                </Column>
                {isAssetEvaluationEnabled && renderContentRuleSelector(!selectedPublisher || !campaignBusinessLine)}
              </Row>

              {noPublisherAccountAvailable && (
                <Alert type="error">{t('publisher_config_none_available')}</Alert>
              )}
            </>
              )
            : existingPublisherConfigItem ?? (isAssetEvaluationEnabled && contentRuleOptions.length > 0)
              ? (
            <Row alignmentVertical="top" widths={['fill', '50%']}>
              <Column spacing="300">
                {existingPublisherConfigItem && (
                  <>
                    <FormSelect
                      control={control}
                      data-testid="campaign-edit-publisher-config-select"
                      disabled
                      label={t('publisher_config')}
                      name="publisherAccountConfigId"
                      options={[{ label: existingPublisherConfigItem.name ?? existingPublisherConfigItem.id, value: existingPublisherConfigItem.id }]}
                    />
                    <PublisherAccountFields hideMinDailyBudget publisher={selectedPublisher} values={existingPublisherConfigItem.config as Record<string, unknown>} />
                  </>
                )}
              </Column>
              {isAssetEvaluationEnabled && contentRuleOptions.length > 0 && renderContentRuleSelector(false)}
            </Row>
                )
              : null}
          </>
        )}

        {isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) && (
          <Row widths={['fill', 'fill']}>
            <FormInput
              control={control}
              data-testid="campaign-edit-name-input"
              disabled={!isCreating}
              label={t('form_global_name')}
              name="name"
              placeholder={t('form_campaign_name_placeholder')}
              required={isCreating}
            />
            <div />
          </Row>
        )}

        {isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) && (
          <Row widths={['fill', 'fill']}>
            <FormSchedule control={control} name="schedule" />
            <div />
          </Row>
        )}

        {isNotificationRecipientsEnabled && (
          <Row data-testid="campaign-notification-recipients-row" widths={['fill', 'fill']}>
            <FormSharedUserInput
              control={control}
              label={t('notification_recipients_header')}
              labelAddon={(
                <Coachmark
                  aria-label={t('notification_recipients_coachmark_title')}
                  data-testid="notification-recipients-coachmark"
                  label={t('notification_recipients_coachmark_title')}
                  popoverPosition="top"
                >
                  <Column width="200px">
                    <Text type="h100">
                      {t('notification_recipients_coachmark_title')}
                    </Text>
                    <Text>{t('notification_recipients_coachmark_body')}</Text>
                  </Column>
                </Coachmark>
              )}
              name="notificationRecipients"
              placeholder={t('notification_recipient_email_placeholder')}
              requireEmail
              testIdPrefix="notificationRecipient-"
              validateEntry={validateNotificationRecipient}
            />
            <div />
          </Row>
        )}

        {isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) && (
          <Row widths={['fill', 'fill']}>
            <FormSelect
              control={control}
              data-testid="campaign-edit-blocklist-select"
              label={t('form_campaigns_blocklist')}
              loading={isBlocklistLoading}
              name="configIds"
              options={filteredBlocklistOptions}
              searchQuery={blocklistSearch}
              onSearch={onBlocklistSearch}
            />
            <div />
          </Row>
        )}

        {!noPublisherAccountAvailable && (
        <FormProductStrategies
          allowedBusinessLines={selectedPublisherConfig?.supportedBusinessLines as string[] | undefined}
          allowedCountries={selectedPublisherConfig?.supportedCountries as string[] | undefined}
          campaignBusinessLine={campaignBusinessLine}
          campaignCountry={campaignCountry}
          control={control}
          disableAdd={isCreating && (!campaignCountry || !campaignBusinessLine)}
          isEditing={!isCreating}
          name="productStrategies"
          publisher={selectedPublisher}
          publisherCountries={publisherCountries}
          publisherTimezone={publisherTimezone}
        />
        )}

        <Row alignmentHorizontal="end">
          <Button
            data-testid="campaign-edit-cancel-button"
            type="secondary"
            onClick={onCancel}
          >
            {t('form_global_cancel')}
          </Button>
          <Button
            data-testid="campaign-edit-save-button"
            disabled={!formState.isValid || isLoading || noPublisherAccountAvailable}
            type="primary"
            onClick={handleSave}
          >
            {t('form_global_save')}
          </Button>
        </Row>
      </FormProvider>
      </Column>
    </Box>
  );
};
